import {
  Activity,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Crosshair,
  Filter,
  Loader2,
  Pencil,
  Plus,
  Search,
  Sparkles,
  TrendingUp,
  Trophy,
  Users,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  fetchActionPlanOverview,
  fetchActionPlans,
  resetActionPlans,
  selectActionPlanOverview,
  selectActionPlansHasMore,
  selectActionPlansItems,
  selectActionPlansPage,
  selectActionPlansStatus,
  updateActionPlan,
  updateActionPlanLocal,
} from "../../store/slices/actionPlansSlice";
import {
  updateJourneyMetric,
  updateOverviewMetric,
} from "../../store/slices/dashboardSlice";
import AcaoCard from "../acaoCard";
import AcaoDetalhe from "../acaoDetalhe";
import Card from "../card";
import * as S from "../dashboard/_shared";
import Section from "../section";
import { numeroOuPadrao } from "@utils/formatters";
import "./style.scss";
import { useChartAnimation } from "../charts/printMode";
import { calcSaldo as calcSaldoMeta, safeGanhoPp } from "./dataTransforms";
import { formatMeta, metaDefinida } from "../../utils/meta";

export default function ActionPlanView({ ctx }) {
  const animarGraficos = useChartAnimation();
  const { C, F, JCFG, JICONS, SC, diasRest, todayStr, ACHIEVEMENT_DEFS, getAchievementStats } = S;
  const LEVELS = [
    { id: 1, name: "Iniciante",     icon: "🌱", color: "#6B7280", bg: "#F3F4F6", min: 0 },
    { id: 2, name: "Intermediário", icon: "⭐", color: "#F59E0B", bg: "#FFFBEB", min: 10 },
    { id: 3, name: "Avançado",      icon: "🏆", color: "#10B981", bg: "#ECFDF5", min: 30 },
    { id: 4, name: "Expert",        icon: "💎", color: "#6366F1", bg: "#EEF2FF", min: 60 },
    { id: 5, name: "Elite",         icon: "🚀", color: "#EF4444", bg: "#FEF2F2", min: 100 },
  ];
  const getSucessoStats = (lista = [], coopPlanosLista = [], coopCurtidasSet = new Set()) => {
    const { totalPp, numSucessos, maxSingle, unlockedBadges } = getAchievementStats(
      lista,
      coopPlanosLista,
      coopCurtidasSet,
    );
    const level = [...LEVELS].reverse().find((l) => totalPp >= l.min) || LEVELS[0];
    const nextLevel = LEVELS.find((l) => l.min > totalPp) || null;
    const progressPct = nextLevel
      ? Math.round(((totalPp - level.min) / (nextLevel.min - level.min)) * 100)
      : 100;
    return { level, nextLevel, totalPp, numSucessos, maxSingle, progressPct, unlockedBadges };
  };
  const {
    acaoSel,
    acoes,
    bulkApplyEtapas,
    bulkApplyGerais,
    bulkOpen,
    bulkPct,
    busca,
    compartilharPlano,
    coopCurtidas,
    coopPlanos,
    delAcao,
    editandoMeta,
    editandoMetaEt,
    filtroResp,
    filtroSt,
    gamificacaoOn,
    metas,
    metasCardOpen,
    metasEtapas,
    openPublishWizard,
    per,
    respDropOpen,
    setAcaoSel,
    setAcoes,
    setBulkApplyEtapas,
    setBulkApplyGerais,
    setBulkOpen,
    setBulkPct,
    setBusca,
    setEditandoMeta,
    setEditandoMetaEt,
    setFiltroResp,
    setFiltroSt,
    setGamificacaoOn,
    setMetaEtapa,
    setMetas,
    setMetasCardOpen,
    setModal,
    setRespDropOpen,
    setStDropOpen,
    setConcluirWizard,
    stDropOpen,
  } = ctx;

  const overviewState = useSelector((state) => state.dashboard.overview);
  const overviewRawData = overviewState?.data?.data;
  const overview = overviewRawData || [[{ respondents: 1 }]];

  const journeyState = useSelector((state) => state.dashboard.journey);
  const journeyRawData = journeyState?.data?.data;
  const journey = journeyRawData || [];

  const dispatch = useDispatch();
  const selectedPeriod = useSelector((state) => state.filters.period);

  // Action plans from Redux
  const apItems = useSelector(selectActionPlansItems);
  const apStatus = useSelector(selectActionPlansStatus);
  const apHasMore = useSelector(selectActionPlansHasMore);
  const apPage = useSelector(selectActionPlansPage);
  const apOverview = useSelector(selectActionPlanOverview);

  // Fetch first page on mount
  useEffect(() => {
    dispatch(resetActionPlans());
    dispatch(fetchActionPlans({ page: 1 }));
    dispatch(fetchActionPlanOverview());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch from page 1 when filters change (skip first render)
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    const stArr = Array.from(filtroSt);
    const respArr = Array.from(filtroResp);
    dispatch(resetActionPlans());
    dispatch(
      fetchActionPlans({
        page: 1,
        search: busca,
        statuses: stArr,
        responsible: respArr[0] || undefined,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, filtroSt, filtroResp]);

  // Infinite scroll sentinel
  const sentinelRef = useRef(null);
  const loadMore = useCallback(() => {
    if (apStatus === "loading" || !apHasMore) return;
    const stArr = Array.from(filtroSt);
    const respArr = Array.from(filtroResp);
    dispatch(
      fetchActionPlans({
        page: apPage + 1,
        search: busca,
        statuses: stArr,
        responsible: respArr[0] || undefined,
      }),
    );
  }, [apStatus, apHasMore, apPage, busca, filtroSt, filtroResp, dispatch]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);
  // Rótulo do período mostrado na coluna "ATUAL" das metas. Deve refletir o
  // período selecionado nos filtros globais (filters.period) — a mesma base
  // usada para buscar overview/journey — e não o periodoG local.
  const periodLabel =
    S.PER?.[selectedPeriod]?.short || selectedPeriod || per?.short || "";

  // Memoized so the useEffect below only fires when real data changes,
  // not on every render due to the `|| {}` fallback creating a new object.
  const overviewItem = useMemo(() => {
    if (!overviewRawData) return {};
    const item = Array.isArray(overviewRawData[0])
      ? overviewRawData[0][0]
      : overviewRawData[0];
    return item || {};
  }, [overviewRawData]);

  useEffect(() => {
    if (overviewItem && Object.keys(overviewItem).length > 0) {
      setMetas({
        nps: overviewItem.nps_target ?? 70,
        jcx: overviewItem.cx_target ?? 73,
        csat: overviewItem.csat_target ?? 79,
        ces: overviewItem.ces_target ?? 66,
      });
    }
    // setMetas is a stable useState setter — safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewItem]);

  useEffect(() => {
    if (journeyRawData && journeyRawData.length > 0) {
      journeyRawData.forEach((e) => {
        setMetaEtapa(e.name, e.target ?? 0);
      });
    }
    // setMetaEtapa is redefined on every parent render — omitting avoids the loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyRawData]);

  // valueOverride permite passar o valor diretamente (necessário no bulk,
  // onde o estado React ainda não foi atualizado no momento da chamada).
  const handleUpdateOverviewMeta = async (k, valueOverride) => {
    const field = k === "jcx" ? "cx_target" : `${k}_target`;
    const val = valueOverride !== undefined ? valueOverride : metas[k];
    const overviewId = overviewItem?.id;
    if (overviewId) {
      try {
        await dispatch(
          updateOverviewMetric({ id: overviewId, payload: { [field]: val } }),
        ).unwrap();
      } catch (e) {
        console.error("Failed to update overview target:", e);
      }
    }
  };

  const handleUpdateJourneyMeta = async (nome, valueOverride) => {
    const val = valueOverride !== undefined ? valueOverride : metasEtapas[nome];
    const stageObj = journey.find((e) => e.name === nome);
    const journeyId =
      stageObj?.id || stageObj?.fk_id_metric || stageObj?.metric?.id;
    if (journeyId) {
      try {
        await dispatch(
          updateJourneyMetric({ id: journeyId, payload: { target: val } }),
        ).unwrap();
      } catch (e) {
        console.error("Failed to update journey stage target:", e);
      }
    }
  };

  const [sucessoModalOpen, setSucessoModalOpen] = useState(false);
  const [sucessoFiltroPeriodo, setSucessoFiltroPeriodo] = useState("todos"); // todos|3m|6m|12m
  const [mostrarTodasConquistas, setMostrarTodasConquistas] = useState(false);

  return (() => {
    const acoesFilt = apItems.filter((a) => {
      const stEmpty = filtroSt.size === 0;
      // Por padrão (sem filtro de status), arquivados ficam ocultos
      if (stEmpty && a.status === "arquivado") return false;
      // Se tem filtro de status, valida OR entre as opções marcadas
      if (!stEmpty) {
        let match = false;
        for (const s of filtroSt) {
          if (s === "concluido_sucesso") {
            if (a.status === "concluido" && a.sucesso) {
              match = true;
              break;
            }
          } else if (s === "concluido_geral") {
            if (a.status === "concluido") {
              match = true;
              break;
            }
          } else if (s === "atrasado") {
            // Atrasado = prazo no passado e ainda não concluído/arquivado
            const hojeStr = todayStr();
            if (
              a.prazo &&
              a.prazo < hojeStr &&
              a.status !== "concluido" &&
              a.status !== "arquivado"
            ) {
              match = true;
              break;
            }
          } else if (a.status === s) {
            match = true;
            break;
          }
        }
        if (!match) return false;
      }
      // Filtro de responsável
      if (filtroResp.size > 0 && !filtroResp.has(a.resp)) return false;
      if (busca && !a.titulo.toLowerCase().includes(busca.toLowerCase()))
        return false;
      return true;
    });
    // Lista única de responsáveis (pra popular o filtro)
    const respsUnicos = Array.from(
      new Set(apItems.map((a) => a.resp).filter(Boolean)),
    ).sort();
    const acaoSelecionada = apItems.find((a) => a.id === acaoSel);
    // Valores atuais (resultado real do período selecionado) — usado pra mostrar saldo vs metas
    const overviewItem =
      (overview &&
        (Array.isArray(overview[0]) ? overview[0][0] : overview[0])) ||
      {};
    const valoresAtuais = {
      jcx: overviewItem?.cx || 0,
      nps: overviewItem?.nps || 0,
      csat: overviewItem?.csat || 0,
      ces: overviewItem?.ces || 0,
    };
    const valorEtapaAtual = (nome) => {
      const et = journey.find((e) => e.name === nome);
      return et ? et.value : 0;
    };
    // Helper saldo: retorna texto formatado + cor baseada no delta atual - meta
    const calcSaldo = (atual, meta, isNps) =>
      calcSaldoMeta(atual, meta, isNps, C);
    // Helper bulk: aplica % de crescimento sobre o atual em todas as metas selecionadas
    const previewBulk = (() => {
      const pct = parseFloat(bulkPct);
      if (isNaN(pct)) return null;
      const fator = 1 + pct / 100;
      const novasGerais = {
        jcx: Math.round(valoresAtuais.jcx * fator),
        nps: Math.round(valoresAtuais.nps * fator),
        csat: Math.round(valoresAtuais.csat * fator),
        ces: Math.round(valoresAtuais.ces * fator),
      };
      const novasEtapas = {};
      Object.keys(JCFG).forEach((nome) => {
        novasEtapas[nome] = Math.round(valorEtapaAtual(nome) * fator);
      });
      const totalAfetadas =
        (bulkApplyGerais ? 4 : 0) + (bulkApplyEtapas ? 11 : 0);
      return { novasGerais, novasEtapas, totalAfetadas, fator };
    })();
    const aplicarBulkMetas = async () => {
      if (!previewBulk) return;
      const pct = parseFloat(bulkPct);
      const msg = `Tem certeza que deseja sobrescrever ${previewBulk.totalAfetadas} metas com base no resultado atual + ${pct}%?\n\nEsta ação NÃO pode ser desfeita automaticamente.`;
      if (!confirm(msg)) return;
      if (bulkApplyGerais) {
        setMetas(previewBulk.novasGerais);
        // Dispara os updates com os valores já calculados (não depende do
        // estado React que ainda não foi commitado).
        await Promise.all(
          Object.keys(previewBulk.novasGerais).map((k) =>
            handleUpdateOverviewMeta(k, previewBulk.novasGerais[k]),
          ),
        );
      }
      if (bulkApplyEtapas) {
        Object.keys(JCFG).forEach((nome) => {
          setMetaEtapa(nome, previewBulk.novasEtapas[nome]);
        });
        await Promise.all(
          Object.keys(JCFG).map((nome) =>
            handleUpdateJourneyMeta(nome, previewBulk.novasEtapas[nome]),
          ),
        );
      }
      setBulkOpen(false);
      setBulkPct("");
    };
    return (
      <div
        className="tabActionPlanView"
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        {/* Metas do plano de ação (editáveis) */}
        <Card
          style={{
            padding: "14px 18px",
            background: `linear-gradient(135deg,${C.greenBg} 0%,${C.surface} 60%)`,
            border: `1px solid ${C.g300}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: metasCardOpen ? 10 : 0,
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  background: C.g700,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Crosshair size={15} color="#FFFFFF" />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.text,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Metas do plano de ação
                </div>
                {metasCardOpen && (
                  <div style={{ fontSize: 10, color: C.text4 }}>
                    Metas configuradas no plano de ação e usadas como referência
                    em todo o dashboard. Clique no número para editar.
                  </div>
                )}
                {!metasCardOpen && (
                  <div style={{ fontSize: 10, color: C.text4 }}>
                    Card minimizado — clique em ↓ para expandir
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* Botão Ajustar todas (bulk) — só aparece quando card está aberto */}
              {metasCardOpen && (
                <button
                  onClick={() => setBulkOpen((o) => !o)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: bulkOpen ? C.g700 : C.surface,
                    color: bulkOpen ? "#FFFFFF" : C.g700,
                    border: `1px solid ${C.g700}`,
                    borderRadius: 10,
                    padding: "7px 13px",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "all .15s",
                    boxShadow: bulkOpen
                      ? "0 2px 6px rgba(22,101,52,.2)"
                      : "none",
                  }}
                >
                  <TrendingUp size={13} /> Ajustar todas as metas
                </button>
              )}
              {/* Botão Minimizar/Expandir */}
              <button
                onClick={() => setMetasCardOpen((o) => !o)}
                title={metasCardOpen ? "Minimizar" : "Expandir"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: C.surface,
                  color: C.g700,
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  width: 32,
                  height: 32,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "all .15s",
                }}
              >
                {metasCardOpen ? (
                  <ChevronUp size={15} />
                ) : (
                  <ChevronDown size={15} />
                )}
              </button>
            </div>
          </div>

          {/* Corpo do card — tudo só aparece quando metasCardOpen=true */}
          {metasCardOpen && (
            <>
              {/* Form expansivo de bulk adjust */}
              {bulkOpen && (
                <div
                  style={{
                    background: C.surface,
                    border: `1.5px solid ${C.g300}`,
                    borderRadius: 12,
                    padding: "14px 16px",
                    marginBottom: 14,
                    boxShadow: "inset 0 1px 3px rgba(0,0,0,.04)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 10,
                    }}
                  >
                    <TrendingUp size={14} color={C.g700} />
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.g700,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Aumentar todas as metas em % sobre o resultado atual
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 14,
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Input do % */}
                    <div style={{ flex: "0 0 auto" }}>
                      <label
                        style={{
                          fontSize: 9,
                          color: C.text4,
                          fontWeight: 600,
                          display: "block",
                          marginBottom: 3,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        % de crescimento
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 2,
                          border: `1.5px solid ${C.g300}`,
                          borderRadius: 8,
                          padding: "6px 10px",
                          background: C.surface,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: C.text3,
                          }}
                        >
                          +
                        </span>
                        <input
                          type="number"
                          value={bulkPct}
                          onChange={(e) => setBulkPct(e.target.value)}
                          placeholder="10"
                          min="0"
                          step="1"
                          autoFocus
                          style={{
                            width: 60,
                            fontSize: 24,
                            fontWeight: 700,
                            color: C.g700,
                            border: "none",
                            outline: "none",
                            fontFamily: "inherit",
                            background: "transparent",
                            padding: 0,
                            letterSpacing: "-0.03em",
                          }}
                        />
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: C.g700,
                          }}
                        >
                          %
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 9,
                          color: C.text4,
                          marginTop: 4,
                          maxWidth: 160,
                          lineHeight: 1.4,
                        }}
                      >
                        Aplica sobre o valor <b>atual</b>, não sobre a meta
                        antiga.
                      </div>
                    </div>

                    {/* Toggles de escopo */}
                    <div
                      style={{
                        flex: "1 1 200px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 9,
                          color: C.text4,
                          fontWeight: 600,
                          display: "block",
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        Aplicar em
                      </label>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          cursor: "pointer",
                          padding: "6px 10px",
                          borderRadius: 8,
                          background: bulkApplyGerais ? C.greenBg : C.surf3,
                          border: `1px solid ${bulkApplyGerais ? C.g300 : C.border}`,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={bulkApplyGerais}
                          onChange={(e) => setBulkApplyGerais(e.target.checked)}
                          style={{ accentColor: C.g700, cursor: "pointer" }}
                        />
                        <span
                          style={{
                            fontSize: 11,
                            color: C.text2,
                            fontWeight: 600,
                          }}
                        >
                          Indicadores gerais (4)
                        </span>
                      </label>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          cursor: "pointer",
                          padding: "6px 10px",
                          borderRadius: 8,
                          background: bulkApplyEtapas ? C.greenBg : C.surf3,
                          border: `1px solid ${bulkApplyEtapas ? C.g300 : C.border}`,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={bulkApplyEtapas}
                          onChange={(e) => setBulkApplyEtapas(e.target.checked)}
                          style={{ accentColor: C.g700, cursor: "pointer" }}
                        />
                        <span
                          style={{
                            fontSize: 11,
                            color: C.text2,
                            fontWeight: 600,
                          }}
                        >
                          Etapas da jornada (11)
                        </span>
                      </label>
                    </div>

                    {/* Preview */}
                    {previewBulk && (bulkApplyGerais || bulkApplyEtapas) && (
                      <div
                        style={{
                          flex: "2 1 280px",
                          background: C.surf3,
                          borderRadius: 10,
                          padding: "10px 14px",
                          border: `1px solid ${C.border}`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            color: C.text4,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 6,
                          }}
                        >
                          Pré-visualização das mudanças
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                        >
                          {bulkApplyGerais &&
                            [
                              { l: "NPS Geral", k: "nps", suf: "" },
                              { l: "Jornada CX", k: "jcx", suf: "%" },
                              { l: "CSAT Médio", k: "csat", suf: "%" },
                              { l: "CES Médio", k: "ces", suf: "%" },
                            ].map((m) => (
                              <div
                                key={m.k}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  fontSize: 10,
                                  color: C.text2,
                                }}
                              >
                                <span style={{ color: C.text3 }}>{m.l}</span>
                                <span style={{ fontFamily: F.mono }}>
                                  atual{" "}
                                  <b style={{ color: C.text }}>
                                    {valoresAtuais[m.k]}
                                    {m.suf}
                                  </b>
                                  <span
                                    style={{ color: C.text4, margin: "0 4px" }}
                                  >
                                    →
                                  </span>
                                  nova meta{" "}
                                  <b style={{ color: C.g700 }}>
                                    {previewBulk.novasGerais[m.k]}
                                    {m.suf}
                                  </b>
                                </span>
                              </div>
                            ))}
                          {bulkApplyEtapas && bulkApplyGerais && (
                            <div
                              style={{
                                height: 1,
                                background: C.border,
                                margin: "4px 0",
                              }}
                            />
                          )}
                          {bulkApplyEtapas && (
                            <div
                              style={{
                                fontSize: 9,
                                color: C.text4,
                                fontStyle: "italic",
                              }}
                            >
                              + as 11 etapas serão ajustadas pela mesma fórmula
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Botões de ação */}
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      justifyContent: "flex-end",
                      marginTop: 14,
                      paddingTop: 12,
                      borderTop: `1px solid ${C.border}`,
                    }}
                  >
                    <button
                      onClick={() => {
                        setBulkOpen(false);
                        setBulkPct("");
                      }}
                      style={{
                        background: "transparent",
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        padding: "8px 16px",
                        fontSize: 11,
                        fontWeight: 600,
                        color: C.text3,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={aplicarBulkMetas}
                      disabled={
                        !previewBulk || (!bulkApplyGerais && !bulkApplyEtapas)
                      }
                      style={{
                        background:
                          !previewBulk || (!bulkApplyGerais && !bulkApplyEtapas)
                            ? C.border
                            : C.g700,
                        color: "#FFFFFF",
                        border: "none",
                        borderRadius: 8,
                        padding: "8px 16px",
                        fontSize: 11,
                        fontWeight: 700,
                        cursor:
                          !previewBulk || (!bulkApplyGerais && !bulkApplyEtapas)
                            ? "default"
                            : "pointer",
                        fontFamily: "inherit",
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                      }}
                    >
                      <CheckCircle2 size={13} /> Confirmar e aplicar a{" "}
                      {previewBulk ? previewBulk.totalAfetadas : 0} metas
                    </button>
                  </div>
                </div>
              )}
              <div
                style={{
                  fontSize: 10,
                  color: C.text3,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 6,
                }}
              >
                Indicadores gerais
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr",
                  gap: 10,
                }}
              >
                {[
                  {
                    k: "nps",
                    l: "NPS Geral",
                    suf: "",
                    color: C.g700,
                    desc: "Net Promoter Score",
                  },
                  {
                    k: "jcx",
                    l: "Jornada CX",
                    suf: "%",
                    color: "#d97706",
                    desc: "média de todos os indicadores",
                  },
                  {
                    k: "csat",
                    l: "CSAT Médio",
                    suf: "%",
                    color: C.g600,
                    desc: "satisfação consolidada",
                  },
                  {
                    k: "ces",
                    l: "CES Médio",
                    suf: "%",
                    color: C.blue,
                    desc: "esforço consolidado",
                  },
                ].map((m) => {
                  const editing = editandoMeta === m.k;
                  const atual = valoresAtuais[m.k];
                  const saldo = calcSaldo(atual, metas[m.k], m.k === "nps");
                  return (
                    <div
                      key={m.k}
                      style={{
                        background: C.surface,
                        borderRadius: 12,
                        padding: "12px 14px",
                        border: `1px solid ${editing ? m.color : C.border}`,
                        transition: "all .15s",
                        boxShadow: editing ? `0 0 0 3px ${m.color}20` : "none",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          color: C.text,
                          fontWeight: 700,
                          letterSpacing: "-0.01em",
                          marginBottom: 8,
                        }}
                      >
                        {m.l}
                      </div>
                      {/* META vs ATUAL — 2 colunas explícitas com divisória */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "stretch",
                          gap: 0,
                        }}
                      >
                        {/* Coluna META */}
                        <div style={{ flex: 1, paddingRight: 12 }}>
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              color: m.color,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              marginBottom: 2,
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <Crosshair size={9} color={m.color} /> Meta
                          </div>
                          {editing ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: 2,
                              }}
                            >
                              <input
                                type="number"
                                autoFocus
                                value={metas[m.k]}
                                onChange={(e) =>
                                  setMetas({
                                    ...metas,
                                    [m.k]: Number(e.target.value) || 0,
                                  })
                                }
                                onBlur={() => {
                                  setEditandoMeta(null);
                                  handleUpdateOverviewMeta(m.k);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === "Escape") {
                                    setEditandoMeta(null);
                                    handleUpdateOverviewMeta(m.k);
                                  }
                                }}
                                style={{
                                  width: 60,
                                  fontSize: 28,
                                  fontWeight: 700,
                                  color: m.color,
                                  letterSpacing: "-0.04em",
                                  border: "none",
                                  outline: "none",
                                  fontFamily: "inherit",
                                  background: "transparent",
                                  padding: 0,
                                }}
                              />
                              <span
                                style={{
                                  fontSize: 18,
                                  fontWeight: 700,
                                  color: m.color,
                                }}
                              >
                                {m.suf}
                              </span>
                            </div>
                          ) : (
                            <div
                              onClick={() => setEditandoMeta(m.k)}
                              style={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: 2,
                                cursor: "pointer",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 28,
                                  fontWeight: 700,
                                  color: m.color,
                                  letterSpacing: "-0.04em",
                                }}
                              >
                                {formatMeta(metas[m.k])}
                              </span>
                              {metaDefinida(metas[m.k]) !== null && (
                                <span
                                  style={{
                                    fontSize: 18,
                                    fontWeight: 700,
                                    color: m.color,
                                  }}
                                >
                                  {m.suf}
                                </span>
                              )}
                              <Pencil
                                size={10}
                                color={C.text4}
                                style={{ marginLeft: 4, opacity: 0.5 }}
                              />
                            </div>
                          )}
                        </div>
                        {/* Divisória vertical */}
                        <div
                          style={{
                            width: 1,
                            background: C.border,
                            marginRight: 12,
                          }}
                        />
                        {/* Coluna ATUAL */}
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              color: C.text3,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              marginBottom: 2,
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <Activity size={9} color={C.text3} /> {periodLabel}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: 2,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 22,
                                fontWeight: 700,
                                color: C.text2,
                                letterSpacing: "-0.03em",
                              }}
                            >
                              {atual}
                            </span>
                            <span
                              style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: C.text2,
                              }}
                            >
                              {m.suf}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: saldo.cor,
                              marginTop: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: 3,
                            }}
                          >
                            {saldo.delta > 0 && <ArrowUpRight size={10} />}
                            {saldo.delta < 0 && <ArrowDownRight size={10} />}
                            {saldo.texto}
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 9,
                          color: C.text4,
                          marginTop: 8,
                          paddingTop: 6,
                          borderTop: `1px solid ${C.border}`,
                        }}
                      >
                        {m.desc}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Metas das 11 etapas da jornada */}
              <div
                style={{
                  marginTop: 18,
                  paddingTop: 14,
                  borderTop: `1px dashed ${C.border}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 10,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 10,
                        color: C.text3,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: 2,
                      }}
                    >
                      Por etapa da jornada
                    </div>
                    <div style={{ fontSize: 10, color: C.text4 }}>
                      11 etapas individuais — cada uma com sua meta CSAT ou CES.
                      Clique no número para editar.
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 10,
                        color: C.text4,
                      }}
                    >
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 2,
                          background: C.g600,
                        }}
                      />
                      CSAT
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 10,
                        color: C.text4,
                      }}
                    >
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 2,
                          background: C.blue,
                        }}
                      />
                      CES
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(210px, 1fr))",
                    gap: 8,
                  }}
                >
                  {Object.keys(JCFG).map((nome) => {
                    const cfg = JCFG[nome];
                    const isCes = cfg.tipo === "ces";
                    const color = isCes ? C.blue : C.g600;
                    const editing = editandoMetaEt === nome;
                    const IconComp = JICONS[nome];
                    const atualEt = valorEtapaAtual(nome);
                    const saldoEt = calcSaldo(
                      atualEt,
                      metasEtapas[nome],
                      false,
                    );
                    return (
                      <div
                        key={nome}
                        style={{
                          background: C.surface,
                          borderRadius: 10,
                          padding: "10px 12px",
                          border: `1px solid ${editing ? color : C.border}`,
                          transition: "all .15s",
                          boxShadow: editing ? `0 0 0 3px ${color}20` : "none",
                          minWidth: 0,
                        }}
                      >
                        {/* Header com ícone e nome */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 6,
                          }}
                        >
                          <div
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: 7,
                              background: isCes ? C.blueBg : C.greenBg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {IconComp && (
                              <IconComp
                                size={12}
                                color={color}
                                strokeWidth={1.8}
                              />
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: C.text2,
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {nome}
                          </div>
                        </div>
                        {/* META | ATUAL com divisória */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "stretch",
                            gap: 0,
                          }}
                        >
                          {/* Coluna META */}
                          <div style={{ flex: 1, paddingRight: 8 }}>
                            <div
                              style={{
                                fontSize: 8,
                                fontWeight: 700,
                                color,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                marginBottom: 1,
                              }}
                            >
                              Meta
                            </div>
                            {editing ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: 1,
                                }}
                              >
                                <input
                                  type="number"
                                  autoFocus
                                  value={metasEtapas[nome]}
                                  onChange={(e) =>
                                    setMetaEtapa(
                                      nome,
                                      Number(e.target.value) || 0,
                                    )
                                  }
                                  onBlur={() => {
                                    setEditandoMetaEt(null);
                                    handleUpdateJourneyMeta(nome);
                                  }}
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === "Enter" ||
                                      e.key === "Escape"
                                    ) {
                                      setEditandoMetaEt(null);
                                      handleUpdateJourneyMeta(nome);
                                    }
                                  }}
                                  style={{
                                    width: 38,
                                    fontSize: 18,
                                    fontWeight: 700,
                                    color,
                                    letterSpacing: "-0.03em",
                                    border: "none",
                                    outline: "none",
                                    fontFamily: "inherit",
                                    background: "transparent",
                                    padding: 0,
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color,
                                  }}
                                >
                                  %
                                </span>
                              </div>
                            ) : (
                              <div
                                onClick={() => setEditandoMetaEt(nome)}
                                style={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: 1,
                                  cursor: "pointer",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 18,
                                    fontWeight: 700,
                                    color,
                                    letterSpacing: "-0.03em",
                                  }}
                                >
                                  {formatMeta(metasEtapas[nome])}
                                </span>
                                {metaDefinida(metasEtapas[nome]) !== null && (
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color,
                                    }}
                                  >
                                    %
                                  </span>
                                )}
                                <Pencil
                                  size={8}
                                  color={C.text4}
                                  style={{ marginLeft: 3, opacity: 0.5 }}
                                />
                              </div>
                            )}
                          </div>
                          {/* Divisória vertical */}
                          <div
                            style={{
                              width: 1,
                              background: C.border,
                              marginRight: 8,
                            }}
                          />
                          {/* Coluna ATUAL */}
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: 8,
                                fontWeight: 700,
                                color: C.text3,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                marginBottom: 1,
                              }}
                            >
                              {periodLabel}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: 1,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 16,
                                  fontWeight: 700,
                                  color: C.text2,
                                  letterSpacing: "-0.03em",
                                }}
                              >
                                {atualEt}
                              </span>
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: C.text2,
                                }}
                              >
                                %
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: saldoEt.cor,
                                marginTop: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              {saldoEt.delta > 0 && <ArrowUpRight size={9} />}
                              {saldoEt.delta < 0 && (
                                <ArrowDownRight size={9} />
                              )}
                              {saldoEt.texto}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Card de Sucessos — modo gamificado ou modo simples (corporativo) */}
        {(() => {
          const sg = getSucessoStats(acoes, coopPlanos, coopCurtidas);
          // Styling muda conforme o modo
          const cardBg = gamificacaoOn
            ? `linear-gradient(135deg,${sg.level.bg} 0%,${C.surface} 55%,${C.greenBg} 100%)`
            : `linear-gradient(135deg,${C.greenBg} 0%,${C.surface} 70%)`;
          const cardBorder = gamificacaoOn ? sg.level.color : C.g300;
          const cardShadow = gamificacaoOn
            ? `0 4px 16px ${sg.level.color}15`
            : "none";

          // Toggle button — sempre visível, em ambos os modos
          const toggleBtn = (
            <button
              onClick={() => setGamificacaoOn((o) => !o)}
              title={
                gamificacaoOn
                  ? "Mudar para modo simples (sem níveis e conquistas)"
                  : "Mudar para modo gamificado (com níveis e conquistas)"
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                background: gamificacaoOn ? C.surface : "rgba(255,255,255,.7)",
                color: gamificacaoOn ? C.g700 : C.text3,
                border: `1px solid ${gamificacaoOn ? sg.level.color + "60" : C.border}`,
                borderRadius: 20,
                padding: "4px 10px",
                fontSize: 10,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all .15s",
                flexShrink: 0,
              }}
            >
              {gamificacaoOn ? (
                <>
                  <Sparkles size={10} /> Gamificação ativa
                </>
              ) : (
                <>
                  <BarChart3 size={10} /> Modo simples
                </>
              )}
            </button>
          );

          // Botão "Ver detalhes" — abre modal com filtros de período + gráfico de evolução + breakdown por etapa
          const verDetalhesBtn = (
            <button
              onClick={() => setSucessoModalOpen(true)}
              title="Ver evolução temporal, filtros de período e breakdown por etapa"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                background: C.greenBg,
                color: C.green,
                border: `1px solid ${C.green}50`,
                borderRadius: 20,
                padding: "4px 10px",
                fontSize: 10,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all .15s",
                flexShrink: 0,
              }}
            >
              <TrendingUp size={10} /> Ver detalhes
            </button>
          );

          const actionBtns = (
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              {verDetalhesBtn}
              {toggleBtn}
            </div>
          );

          return (
            <Card
              style={{
                padding: "16px 20px",
                background: cardBg,
                border: `1.5px solid ${cardBorder}`,
                boxShadow: cardShadow,
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Efeito decorativo de fundo (só no modo gamificado) */}
              {gamificacaoOn && (
                <div
                  style={{
                    position: "absolute",
                    top: -20,
                    right: -10,
                    fontSize: 120,
                    opacity: 0.07,
                    lineHeight: 1,
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                >
                  {sg.level.icon}
                </div>
              )}

              {gamificacaoOn ? (
                /* ───── MODO GAMIFICADO ───── */
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 14,
                      marginBottom: 12,
                      position: "relative",
                    }}
                  >
                    {/* Emoji do nível */}
                    <div
                      style={{
                        width: 58,
                        height: 58,
                        borderRadius: 16,
                        background: `linear-gradient(135deg,${sg.level.bg},#FFFFFF)`,
                        border: `2px solid ${sg.level.color}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 32,
                        flexShrink: 0,
                        boxShadow: `0 4px 12px ${sg.level.color}25`,
                      }}
                    >
                      {sg.level.icon}
                    </div>
                    {/* Nível e total */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.g700,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <Trophy size={11} color={C.green} /> Sucessos do plano
                          de ação
                        </div>
                        <div
                          style={{
                            padding: "2px 8px",
                            borderRadius: 20,
                            background: sg.level.color,
                            color: "#FFFFFF",
                            fontSize: 9,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}
                        >
                          Nível {sg.level.id}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 10,
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: sg.level.color,
                            letterSpacing: "-0.03em",
                            lineHeight: 1,
                          }}
                        >
                          {sg.level.name}
                        </div>
                        <div
                          style={{
                            fontSize: 26,
                            fontWeight: 700,
                            color: C.green,
                            letterSpacing: "-0.04em",
                            lineHeight: 1,
                          }}
                        >
                          +{sg.totalPp}pp
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: C.text3,
                            fontWeight: 500,
                          }}
                        >
                          {sg.numSucessos}{" "}
                          {sg.numSucessos === 1
                            ? "ação com sucesso"
                            : "ações com sucesso"}
                        </div>
                      </div>
                    </div>
                    {/* Botões de ação */}
                    {actionBtns}
                  </div>

                  {/* Progress bar pro próximo nível */}
                  {sg.nextLevel ? (
                    <div style={{ marginBottom: 12, position: "relative" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontSize: 10,
                          color: C.text3,
                          marginBottom: 4,
                          fontWeight: 500,
                        }}
                      >
                        <span>
                          Progresso para{" "}
                          <b style={{ color: sg.nextLevel.color }}>
                            {sg.nextLevel.icon} {sg.nextLevel.name}
                          </b>
                        </span>
                        <span>
                          faltam{" "}
                          <b style={{ color: C.text }}>
                            +{sg.nextLevel.min - sg.totalPp}pp
                          </b>
                        </span>
                      </div>
                      <div
                        style={{
                          height: 8,
                          background: C.surf3,
                          borderRadius: 20,
                          overflow: "hidden",
                          border: `1px solid ${C.border}`,
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${sg.progressPct}%`,
                            background: `linear-gradient(90deg,${sg.level.color},${sg.nextLevel.color})`,
                            borderRadius: 20,
                            transition: "width .5s ease",
                            boxShadow: `0 0 8px ${sg.level.color}60`,
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        marginBottom: 12,
                        padding: "8px 12px",
                        background: sg.level.bg,
                        borderRadius: 8,
                        border: `1px solid ${sg.level.color}40`,
                        textAlign: "center",
                        fontSize: 11,
                        color: sg.level.color,
                        fontWeight: 700,
                      }}
                    >
                      👑 Nível máximo atingido — você é uma lenda do CX!
                    </div>
                  )}

                  {/* Badges (conquistas) — por padrão só mostra as desbloqueadas;
                      a lista completa (com as bloqueadas) fica atrás de "Ver todas" */}
                  <div>
                    <div
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.text3,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: 6,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <Sparkles size={10} color={C.g700} /> Conquistas
                      <span
                        style={{
                          color: C.text4,
                          fontWeight: 500,
                          textTransform: "none",
                          letterSpacing: "normal",
                        }}
                      >
                        · {sg.unlockedBadges.length}/{ACHIEVEMENT_DEFS.length}{" "}
                        desbloqueadas
                      </span>
                      <button
                        onClick={() => setMostrarTodasConquistas((v) => !v)}
                        style={{
                          marginLeft: "auto",
                          display: "flex",
                          alignItems: "center",
                          gap: 3,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: C.g700,
                          fontWeight: 700,
                          fontSize: 9,
                          textTransform: "none",
                          letterSpacing: "normal",
                          padding: 0,
                          fontFamily: "inherit",
                        }}
                      >
                        {mostrarTodasConquistas ? "Ver só desbloqueadas" : "Ver todas"}
                        {mostrarTodasConquistas ? (
                          <ChevronUp size={11} />
                        ) : (
                          <ChevronDown size={11} />
                        )}
                      </button>
                    </div>

                    {!mostrarTodasConquistas ? (
                      sg.unlockedBadges.length === 0 ? (
                        <div style={{ fontSize: 10, color: C.text4, fontStyle: "italic" }}>
                          Nenhuma conquista desbloqueada ainda — comece registrando o
                          sucesso do primeiro plano.
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {sg.unlockedBadges.map((b) => (
                            <div
                              key={b.id}
                              title={`${b.name} — ${b.desc}`}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                padding: "5px 10px",
                                borderRadius: 20,
                                background: C.greenBg,
                                border: `1px solid ${C.green}60`,
                                cursor: "default",
                              }}
                            >
                              <span style={{ fontSize: 13 }}>{b.icon}</span>
                              <span style={{ fontSize: 10, fontWeight: 600, color: C.text2, whiteSpace: "nowrap" }}>
                                {b.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      )
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {Object.entries(
                          ACHIEVEMENT_DEFS.reduce((acc, b) => {
                            (acc[b.cat] ||= []).push(b);
                            return acc;
                          }, {}),
                        ).map(([cat, badges]) => (
                          <div key={cat}>
                            <div style={{ fontSize: 9, fontWeight: 700, color: C.text4, marginBottom: 4 }}>
                              {cat}
                            </div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              {badges.map((b) => {
                                const unlocked = sg.unlockedBadges.some((ub) => ub.id === b.id);
                                return (
                                  <div
                                    key={b.id}
                                    title={`${b.name} — ${b.desc}${unlocked ? "" : " (bloqueada)"}`}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 5,
                                      padding: "5px 10px",
                                      borderRadius: 20,
                                      background: unlocked ? C.greenBg : "#F9FAFB",
                                      border: `1px solid ${unlocked ? C.green + "60" : "#E5E7EB"}`,
                                      cursor: "default",
                                      opacity: unlocked ? 1 : 0.55,
                                      transition: "all .15s",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 13,
                                        filter: unlocked ? "none" : "grayscale(100%)",
                                      }}
                                    >
                                      {b.icon}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 600,
                                        color: unlocked ? C.text2 : C.text4,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {b.name}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* ───── MODO SIMPLES (corporativo) ───── */
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  {/* Ícone neutro */}
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: C.g700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Trophy size={22} color="#FFFFFF" />
                  </div>
                  {/* Stats */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.g700,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        marginBottom: 4,
                      }}
                    >
                      Sucessos do plano de ação
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 12,
                        flexWrap: "wrap",
                        marginBottom: 2,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 28,
                          fontWeight: 700,
                          color: C.green,
                          letterSpacing: "-0.04em",
                          lineHeight: 1,
                        }}
                      >
                        +{sg.totalPp}pp
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: C.text3,
                          fontWeight: 500,
                        }}
                      >
                        de impacto cumulado
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: C.text3 }}>
                      <b style={{ color: C.text2 }}>{sg.numSucessos}</b>{" "}
                      {sg.numSucessos === 1
                        ? "ação concluída com sucesso registrado"
                        : "ações concluídas com sucesso registrado"}
                      {sg.maxSingle > 0 && (
                        <>
                          {" "}
                          · maior ganho individual:{" "}
                          <b style={{ color: C.text2 }}>+{sg.maxSingle}pp</b>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Botões de ação */}
                  {actionBtns}
                </div>
              )}
            </Card>
          );
        })()}

        {/* Summary */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button
            onClick={() => setModal("new")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: C.g700,
              color: "#FFFFFF",
              border: "none",
              borderRadius: 14,
              padding: "12px 20px 12px 14px",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
              flexShrink: 0,
              boxShadow: "0 2px 8px rgba(22,101,52,.25)",
              transition: "all .15s",
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 10,
                background: "rgba(255,255,255,.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ClipboardList size={16} color="#FFFFFF" strokeWidth={2.2} />
            </div>
            Nova ação
            <Plus size={14} strokeWidth={2.8} />
          </button>
          {[
            { l: "Total", v: apOverview.total, c: C.text },
            { l: "Concluídas", v: apOverview.concluidas, c: C.green },
            { l: "Em andamento", v: apOverview.em_andamento, c: C.blue },
            { l: "Atrasadas", v: apOverview.atrasadas, c: C.red },
          ].map((k, i) => (
            <Card
              key={i}
              style={{ flex: 1, minWidth: 110, padding: "14px 16px" }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: C.text4,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                {k.l}
              </div>
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: k.c,
                  letterSpacing: "-0.04em",
                }}
              >
                {k.v}
              </div>
            </Card>
          ))}
        </div>
        {/* Filters */}
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              padding: "7px 12px",
              background: C.surface,
            }}
          >
            <Search size={13} color={C.text4} />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar..."
              style={{
                border: "none",
                outline: "none",
                fontSize: 12,
                background: "transparent",
                color: C.text,
                fontFamily: "inherit",
                minWidth: 120,
              }}
            />
          </div>

          {/* Filtro de Status (multi-select) */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => {
                setStDropOpen((o) => !o);
                setRespDropOpen(false);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                border: `1px solid ${stDropOpen || filtroSt.size > 0 ? C.g700 : C.border}`,
                borderRadius: 10,
                padding: "7px 12px",
                fontSize: 12,
                background: C.surface,
                fontFamily: "inherit",
                color: C.text,
                cursor: "pointer",
                fontWeight: filtroSt.size > 0 ? 600 : 400,
              }}
            >
              <Filter size={12} color={filtroSt.size > 0 ? C.g700 : C.text3} />
              Status
              {filtroSt.size > 0 && (
                <span
                  style={{
                    background: C.g700,
                    color: "#FFFFFF",
                    borderRadius: 20,
                    padding: "1px 7px",
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {filtroSt.size}
                </span>
              )}
              <ChevronDown
                size={12}
                color={C.text3}
                style={{
                  transform: stDropOpen ? "rotate(180deg)" : "none",
                  transition: "transform .15s",
                }}
              />
            </button>
            {stDropOpen && (
              <>
                <div
                  onClick={() => setStDropOpen(false)}
                  style={{ position: "fixed", inset: 0, zIndex: 90 }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    left: 0,
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 12,
                    boxShadow: "0 12px 32px rgba(0,0,0,.12)",
                    padding: "8px 0",
                    minWidth: 240,
                    zIndex: 91,
                  }}
                >
                  <div
                    style={{
                      padding: "4px 14px 8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderBottom: `1px solid ${C.border}`,
                      marginBottom: 4,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.text3,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Filtrar por status
                    </div>
                    {filtroSt.size > 0 && (
                      <button
                        onClick={() => setFiltroSt(new Set())}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: 10,
                          color: C.g700,
                          fontWeight: 600,
                          fontFamily: "inherit",
                        }}
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                  {[
                    { k: "pendente", lbl: "Pendente", dot: SC.pendente.color },
                    {
                      k: "em_andamento",
                      lbl: "Em andamento",
                      dot: SC.em_andamento.color,
                    },
                    { k: "atrasado", lbl: "⚠️ Atrasados", dot: C.red },
                    {
                      k: "concluido_geral",
                      lbl: "Concluídos (Geral)",
                      dot: SC.concluido.color,
                    },
                    {
                      k: "concluido_sucesso",
                      lbl: "🏆 Concluídos com Sucesso",
                      dot: C.green,
                    },
                    { k: "cancelado", lbl: "Cancelado", dot: SC.cancelado.color },
                    {
                      k: "arquivado",
                      lbl: "Arquivado",
                      dot: SC.arquivado.color,
                    },
                  ].map((opt) => {
                    const checked = filtroSt.has(opt.k);
                    return (
                      <label
                        key={opt.k}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "8px 14px",
                          cursor: "pointer",
                          fontSize: 12,
                          color: C.text,
                          background: checked ? C.greenBg : "transparent",
                        }}
                        onClick={() => {
                          const next = new Set(filtroSt);
                          if (next.has(opt.k)) next.delete(opt.k);
                          else next.add(opt.k);
                          setFiltroSt(next);
                        }}
                      >
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            border: `1.5px solid ${checked ? C.g700 : C.border2}`,
                            background: checked ? C.g700 : C.surface,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {checked && (
                            <svg
                              width="10"
                              height="8"
                              viewBox="0 0 10 8"
                              fill="none"
                            >
                              <path
                                d="M1 4L3.5 6.5L9 1"
                                stroke="white"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: opt.dot,
                            flexShrink: 0,
                          }}
                        />
                        <span>{opt.lbl}</span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Filtro de Responsável (multi-select) */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => {
                setRespDropOpen((o) => !o);
                setStDropOpen(false);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                border: `1px solid ${respDropOpen || filtroResp.size > 0 ? C.purple : C.border}`,
                borderRadius: 10,
                padding: "7px 12px",
                fontSize: 12,
                background: C.surface,
                fontFamily: "inherit",
                color: C.text,
                cursor: "pointer",
                fontWeight: filtroResp.size > 0 ? 600 : 400,
              }}
            >
              <Users
                size={12}
                color={filtroResp.size > 0 ? C.purple : C.text3}
              />
              Responsável
              {filtroResp.size > 0 && (
                <span
                  style={{
                    background: C.purple,
                    color: "#FFFFFF",
                    borderRadius: 20,
                    padding: "1px 7px",
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {filtroResp.size}
                </span>
              )}
              <ChevronDown
                size={12}
                color={C.text3}
                style={{
                  transform: respDropOpen ? "rotate(180deg)" : "none",
                  transition: "transform .15s",
                }}
              />
            </button>
            {respDropOpen && (
              <>
                <div
                  onClick={() => setRespDropOpen(false)}
                  style={{ position: "fixed", inset: 0, zIndex: 90 }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    left: 0,
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 12,
                    boxShadow: "0 12px 32px rgba(0,0,0,.12)",
                    padding: "8px 0",
                    minWidth: 260,
                    maxHeight: 340,
                    overflowY: "auto",
                    zIndex: 91,
                  }}
                >
                  <div
                    style={{
                      padding: "4px 14px 8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderBottom: `1px solid ${C.border}`,
                      marginBottom: 4,
                      position: "sticky",
                      top: 0,
                      background: C.surface,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.text3,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Filtrar por responsável
                    </div>
                    {filtroResp.size > 0 && (
                      <button
                        onClick={() => setFiltroResp(new Set())}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: 10,
                          color: C.purple,
                          fontWeight: 600,
                          fontFamily: "inherit",
                        }}
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                  {respsUnicos.length === 0 && (
                    <div
                      style={{
                        padding: "12px 14px",
                        fontSize: 11,
                        color: C.text4,
                        fontStyle: "italic",
                      }}
                    >
                      Nenhum responsável cadastrado
                    </div>
                  )}
                  {respsUnicos.map((r) => {
                    const checked = filtroResp.has(r);
                    const countResp = apItems.filter(
                      (a) => a.resp === r,
                    ).length;
                    return (
                      <label
                        key={r}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "8px 14px",
                          cursor: "pointer",
                          fontSize: 12,
                          color: C.text,
                          background: checked ? "#F8F0FF" : "transparent",
                        }}
                        onClick={() => {
                          const next = new Set(filtroResp);
                          if (next.has(r)) next.delete(r);
                          else next.add(r);
                          setFiltroResp(next);
                        }}
                      >
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            border: `1.5px solid ${checked ? C.purple : C.border2}`,
                            background: checked ? C.purple : C.surface,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {checked && (
                            <svg
                              width="10"
                              height="8"
                              viewBox="0 0 10 8"
                              fill="none"
                            >
                              <path
                                d="M1 4L3.5 6.5L9 1"
                                stroke="white"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                        <span
                          style={{
                            flex: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {r}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            color: C.text4,
                            fontWeight: 500,
                          }}
                        >
                          {countResp}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <span style={{ fontSize: 11, color: C.text4, marginLeft: "auto" }}>
            {acoesFilt.length} de {apOverview.total} ações
          </span>
        </div>
        {/* Cards + detalhe */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: acaoSelecionada
              ? "320px 1fr"
              : "repeat(2,1fr)",
            gap: 14,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {acoesFilt.map((a) => (
              <AcaoCard
                key={a.id}
                a={a}
                sel={acaoSel === a.id}
                onSel={(id) => setAcaoSel(id)}
                onEdit={(a) => setModal(a)}
                onDel={delAcao}
              />
            ))}
            {acoesFilt.length === 0 && apStatus !== "loading" && (
              <Card style={{ padding: "32px", textAlign: "center" }}>
                <div style={{ color: C.text4, fontSize: 13 }}>
                  Nenhuma ação encontrada
                </div>
              </Card>
            )}
            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {apStatus === "loading" && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "12px 0",
                  color: C.text4,
                  fontSize: 12,
                }}
              >
                <Loader2
                  size={14}
                  style={{ animation: "spin 1s linear infinite" }}
                />
                Carregando...
              </div>
            )}
            {!apHasMore && acoesFilt.length > 0 && apStatus !== "loading" && (
              <div
                style={{
                  textAlign: "center",
                  fontSize: 11,
                  color: C.text4,
                  padding: "8px 0",
                  fontStyle: "italic",
                }}
              >
                Todos os planos carregados
              </div>
            )}
          </div>
          {acaoSelecionada && (
            <AcaoDetalhe
              a={acaoSelecionada}
              onClose={() => setAcaoSel(null)}
              onUpdate={(u) => {
                const oldStatus = acaoSelecionada?.status;
                setAcoes((prev) => {
                  const exists = prev.some((x) => x.id === u.id);
                  return exists ? prev.map((a) => (a.id === u.id ? u : a)) : [...prev, u];
                });
                dispatch(updateActionPlanLocal(u));
                // "arquivado" é conceito local — não existe no backend, só oculta da lista padrão
                if (u._apiId && u.status !== "arquivado") {
                  dispatch(updateActionPlan(u)).unwrap().then(() => {
                    // Dispara wizard de sucesso se a ação acabou de ser concluída
                    if (
                      oldStatus !== "concluido" &&
                      u.status === "concluido" &&
                      !u.sucesso &&
                      setConcluirWizard
                    ) {
                      const ov = overviewItem || {};
                      const valMap = {
                        "NPS Geral": ov.nps ?? 0,
                        "CSAT Geral": ov.csat ?? 0,
                        "CES Geral": ov.ces ?? 0,
                      };
                      journey.forEach((e) => { if (e.name) valMap[e.name] = e.value ?? 0; });
                      const valorDash =
                        valMap[u.etapa] ?? numeroOuPadrao(u.v_atual, 0);
                      const ganhoAuto =
                        Math.round(
                          (valorDash - numeroOuPadrao(u.v_baseline, valorDash)) * 10,
                        ) / 10;
                      setConcluirWizard({
                        step: "registrar_sucesso",
                        acao: { ...u, v_atual: valorDash },
                        ganhoAuto,
                        ganhoEdit: String(Math.max(0, ganhoAuto)),
                        obs: "",
                        autorPub: u.resp || "",
                        tituloPub: u.titulo,
                        descPub: u.desc || "",
                        telefonePub: "",
                        emailPub: "",
                      });
                    }
                  }).catch((err) => {
                    alert(`Erro ao salvar alteração:\n${err}`);
                  });
                }
              }}
              onShare={compartilharPlano}
              onPublishWizard={openPublishWizard}
            />
          )}
        </div>
        {/* Modal de Detalhes do Sucesso do Plano de Ação */}
        {sucessoModalOpen &&
          (() => {
            // Filtra ações com sucesso registrado
            const todasComSucesso = acoes.filter((a) => a.sucesso);

            // Aplica filtro de período baseado em data registradoEm
            const hoje = new Date();
            const filtroEm = (a) => {
              if (sucessoFiltroPeriodo === "todos") return true;
              const dataReg = a.sucesso?.registradoEm
                ? new Date(a.sucesso.registradoEm)
                : null;
              if (!dataReg) return sucessoFiltroPeriodo === "todos";
              const diffMeses = (hoje - dataReg) / (1000 * 60 * 60 * 24 * 30);
              if (sucessoFiltroPeriodo === "3m") return diffMeses <= 3;
              if (sucessoFiltroPeriodo === "6m") return diffMeses <= 6;
              if (sucessoFiltroPeriodo === "12m") return diffMeses <= 12;
              return true;
            };

            const sucessosFiltrados = todasComSucesso.filter(filtroEm);
            const totalPpFiltrado = sucessosFiltrados.reduce(
              (s, a) => s + safeGanhoPp(a.sucesso.ganhoPp),
              0,
            );
            const numFiltrado = sucessosFiltrados.length;
            const maiorIndividual = sucessosFiltrados.reduce(
              (m, a) => Math.max(m, safeGanhoPp(a.sucesso.ganhoPp)),
              0,
            );

            // Breakdown por etapa: agrupa por a.etapa
            const porEtapa = {};
            sucessosFiltrados.forEach((a) => {
              const et = a.etapa || "Outras";
              if (!porEtapa[et])
                porEtapa[et] = { etapa: et, count: 0, totalPp: 0, acoes: [] };
              porEtapa[et].count += 1;
              porEtapa[et].totalPp += safeGanhoPp(a.sucesso.ganhoPp);
              porEtapa[et].acoes.push(a);
            });
            const breakdownEtapa = Object.values(porEtapa).sort(
              (a, b) => b.totalPp - a.totalPp,
            );

            // Evolução cumulativa: ordena por data e acumula
            const ordenadosPorData = [...sucessosFiltrados]
              .filter((a) => a.sucesso?.registradoEm)
              .sort(
                (a, b) =>
                  new Date(a.sucesso.registradoEm) -
                  new Date(b.sucesso.registradoEm),
              );
            let acumulado = 0;
            const evolucaoData = ordenadosPorData.map((a) => {
              acumulado += safeGanhoPp(a.sucesso.ganhoPp);
              const d = new Date(a.sucesso.registradoEm);
              return {
                data: `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`,
                dataFull: a.sucesso.registradoEm,
                acumulado,
                ganho: safeGanhoPp(a.sucesso.ganhoPp),
                titulo: a.titulo,
                etapa: a.etapa,
              };
            });

            const PERIODO_OPTS = [
              { v: "todos", label: "Todo histórico" },
              { v: "12m", label: "Últimos 12 meses" },
              { v: "6m", label: "Últimos 6 meses" },
              { v: "3m", label: "Últimos 3 meses" },
            ];

            return (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(0,0,0,.55)",
                  backdropFilter: "blur(6px)",
                  zIndex: 1000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 20,
                }}
              >
                <div
                  style={{
                    background: C.surface,
                    borderRadius: 18,
                    maxWidth: 920,
                    width: "100%",
                    maxHeight: "90vh",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 24px 64px rgba(0,0,0,.3)",
                    overflow: "hidden",
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      padding: "18px 22px",
                      borderBottom: `1px solid ${C.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background: `linear-gradient(135deg,${C.greenBg} 0%,${C.surface} 100%)`,
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          background: C.green,
                          borderRadius: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Trophy size={20} color="#FFFFFF" />
                      </div>
                      <div>
                        <h3
                          style={{
                            margin: 0,
                            fontSize: 16,
                            fontWeight: 700,
                            color: C.text,
                            letterSpacing: "-0.02em",
                          }}
                        >
                          Sucessos do plano de ação
                        </h3>
                        <div
                          style={{ fontSize: 11, color: C.text4, marginTop: 2 }}
                        >
                          Evolução cumulativa, breakdown por etapa, histórico
                          completo
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setSucessoModalOpen(false)}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: 6,
                        borderRadius: 6,
                        color: C.text4,
                        display: "flex",
                      }}
                    >
                      <XCircle size={22} />
                    </button>
                  </div>

                  {/* Filtros de período */}
                  <div
                    style={{
                      padding: "14px 22px",
                      borderBottom: `1px solid ${C.border}`,
                      background: C.surf3,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.text3,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Período
                    </span>
                    <div
                      style={{
                        display: "flex",
                        gap: 4,
                        background: C.surface,
                        borderRadius: 9,
                        padding: 3,
                        border: `1px solid ${C.border}`,
                      }}
                    >
                      {PERIODO_OPTS.map((opt) => (
                        <button
                          key={opt.v}
                          onClick={() => setSucessoFiltroPeriodo(opt.v)}
                          style={{
                            background:
                              sucessoFiltroPeriodo === opt.v
                                ? C.g700
                                : "transparent",
                            color:
                              sucessoFiltroPeriodo === opt.v
                                ? "#FFFFFF"
                                : C.text2,
                            border: "none",
                            borderRadius: 6,
                            padding: "5px 11px",
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: "pointer",
                            fontFamily: "inherit",
                            transition: "all .15s",
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <div style={{ flex: 1 }} />
                    <div style={{ fontSize: 10, color: C.text4 }}>
                      Mostrando <b style={{ color: C.text2 }}>{numFiltrado}</b>{" "}
                      {numFiltrado === 1 ? "sucesso" : "sucessos"}
                    </div>
                  </div>

                  {/* Body scroll */}
                  <div
                    style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}
                  >
                    {/* KPIs */}
                    <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
                      <div
                        style={{
                          flex: 1,
                          padding: "14px 16px",
                          background: C.greenBg,
                          borderRadius: 10,
                          border: `1px solid ${C.green}30`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            color: C.green,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            marginBottom: 4,
                          }}
                        >
                          Impacto cumulado
                        </div>
                        <div
                          style={{
                            fontSize: 26,
                            fontWeight: 700,
                            color: C.green,
                            letterSpacing: "-0.03em",
                            lineHeight: 1,
                          }}
                        >
                          +{totalPpFiltrado}pp
                        </div>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          padding: "14px 16px",
                          background: C.surf3,
                          borderRadius: 10,
                          border: `1px solid ${C.border}`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            color: C.text3,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            marginBottom: 4,
                          }}
                        >
                          Ações com sucesso
                        </div>
                        <div
                          style={{
                            fontSize: 26,
                            fontWeight: 700,
                            color: C.text,
                            letterSpacing: "-0.03em",
                            lineHeight: 1,
                          }}
                        >
                          {numFiltrado}
                        </div>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          padding: "14px 16px",
                          background: C.surf3,
                          borderRadius: 10,
                          border: `1px solid ${C.border}`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            color: C.text3,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            marginBottom: 4,
                          }}
                        >
                          Maior ganho individual
                        </div>
                        <div
                          style={{
                            fontSize: 26,
                            fontWeight: 700,
                            color: C.text,
                            letterSpacing: "-0.03em",
                            lineHeight: 1,
                          }}
                        >
                          +{maiorIndividual}pp
                        </div>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          padding: "14px 16px",
                          background: C.surf3,
                          borderRadius: 10,
                          border: `1px solid ${C.border}`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            color: C.text3,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            marginBottom: 4,
                          }}
                        >
                          Etapas impactadas
                        </div>
                        <div
                          style={{
                            fontSize: 26,
                            fontWeight: 700,
                            color: C.text,
                            letterSpacing: "-0.03em",
                            lineHeight: 1,
                          }}
                        >
                          {breakdownEtapa.length}
                        </div>
                      </div>
                    </div>

                    {/* Gráfico de evolução cumulativa */}
                    {evolucaoData.length > 0 ? (
                      <Section title="Evolução cumulativa do impacto">
                        <div style={{ padding: "4px 6px" }}>
                          <ResponsiveContainer width="100%" height={240}>
                            <LineChart
                              data={evolucaoData}
                              margin={{
                                top: 10,
                                right: 20,
                                left: 0,
                                bottom: 0,
                              }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={C.border}
                                vertical={false}
                              />
                              <XAxis
                                dataKey="data"
                                tick={{ fontSize: 11, fill: C.text3 }}
                              />
                              <YAxis
                                tick={{ fontSize: 10, fill: C.text4 }}
                                label={{
                                  value: "pp acumulados",
                                  angle: -90,
                                  position: "insideLeft",
                                  fill: C.text4,
                                  fontSize: 10,
                                }}
                              />
                              <Tooltip
                                contentStyle={{
                                  background: C.surface,
                                  border: `1px solid ${C.border}`,
                                  borderRadius: 8,
                                  fontSize: 11,
                                }}
                                formatter={(value, name, props) => [
                                  `+${value}pp`,
                                  "Acumulado",
                                ]}
                                labelFormatter={(label, payload) => {
                                  if (payload && payload[0]) {
                                    const p = payload[0].payload;
                                    return `${label} — ${p.titulo} (+${p.ganho}pp em ${p.etapa})`;
                                  }
                                  return label;
                                }}
                              />
                              <Line
                                isAnimationActive={animarGraficos}
                                type="monotone"
                                dataKey="acumulado"
                                stroke={C.green}
                                strokeWidth={3}
                                dot={{ r: 5, fill: C.green, strokeWidth: 0 }}
                                activeDot={{ r: 7 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </Section>
                    ) : (
                      <div
                        style={{
                          padding: "30px",
                          textAlign: "center",
                          color: C.text4,
                          fontSize: 12,
                          fontStyle: "italic",
                          background: C.surf3,
                          borderRadius: 10,
                        }}
                      >
                        Sem sucessos registrados no período selecionado.
                      </div>
                    )}

                    {/* Breakdown por etapa */}
                    {breakdownEtapa.length > 0 && (
                      <div style={{ marginTop: 18 }}>
                        <Section title="Sucessos por etapa da jornada">
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                          >
                            {breakdownEtapa.map((b, i) => {
                              const pctMax = breakdownEtapa[0].totalPp;
                              const widthPct =
                                pctMax > 0
                                  ? Math.round((b.totalPp / pctMax) * 100)
                                  : 0;
                              return (
                                <div
                                  key={b.etapa}
                                  style={{
                                    padding: "11px 14px",
                                    background: C.surf3,
                                    borderRadius: 9,
                                    border: `1px solid ${C.border}`,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: 6,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: C.text,
                                      }}
                                    >
                                      {b.etapa}
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 10,
                                        alignItems: "center",
                                      }}
                                    >
                                      <span
                                        style={{ fontSize: 10, color: C.text4 }}
                                      >
                                        {b.count}{" "}
                                        {b.count === 1 ? "ação" : "ações"}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 14,
                                          fontWeight: 700,
                                          color: C.green,
                                          letterSpacing: "-0.02em",
                                        }}
                                      >
                                        +{b.totalPp}pp
                                      </span>
                                    </div>
                                  </div>
                                  {/* Barra de proporção */}
                                  <div
                                    style={{
                                      height: 5,
                                      background: C.surface,
                                      borderRadius: 3,
                                      overflow: "hidden",
                                    }}
                                  >
                                    <div
                                      style={{
                                        height: "100%",
                                        width: `${widthPct}%`,
                                        background: `linear-gradient(90deg,${C.green},${C.g700})`,
                                        borderRadius: 3,
                                        transition: "width .5s",
                                      }}
                                    />
                                  </div>
                                  {/* Lista das ações */}
                                  <div
                                    style={{
                                      marginTop: 8,
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 3,
                                    }}
                                  >
                                    {b.acoes.slice(0, 3).map((a, j) => (
                                      <div
                                        key={j}
                                        style={{
                                          fontSize: 10,
                                          color: C.text3,
                                          display: "flex",
                                          justifyContent: "space-between",
                                          gap: 8,
                                        }}
                                      >
                                        <span
                                          style={{
                                            flex: 1,
                                            minWidth: 0,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {a.titulo}
                                        </span>
                                        <span
                                          style={{
                                            color: C.green,
                                            fontWeight: 600,
                                            flexShrink: 0,
                                          }}
                                        >
                                          +{safeGanhoPp(a.sucesso.ganhoPp)}pp
                                        </span>
                                      </div>
                                    ))}
                                    {b.acoes.length > 3 && (
                                      <div
                                        style={{
                                          fontSize: 10,
                                          color: C.text4,
                                          fontStyle: "italic",
                                        }}
                                      >
                                        +{b.acoes.length - 3} ações adicionais
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </Section>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div
                    style={{
                      padding: "12px 22px",
                      borderTop: `1px solid ${C.border}`,
                      background: C.surf3,
                      fontSize: 10,
                      color: C.text4,
                      fontStyle: "italic",
                      lineHeight: 1.5,
                      flexShrink: 0,
                    }}
                  >
                    ⓘ O impacto cumulado soma os ganhos em pontos percentuais
                    (pp) das ações concluídas com sucesso registrado. Use os
                    filtros para isolar períodos específicos e identificar quais
                    etapas da jornada concentram seus melhores resultados.
                  </div>
                </div>
              </div>
            );
          })()}
      </div>
    );
  })();
}
